import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { ApplicationState } from '@/state/root.models'

import {
  FormStatusType,
  TriggerConditions,
  BonusType,
  ExpirationType,
  BonusPeriodRecurrenceEnum,
  BonusModeType,
  BonusWageringOrderType,
  BonusTriggerType,
} from '@/enums'
import keys from '@/utils/keys'
import { getIsoLocalTimeFromDateTime } from '@/utils/date'
import { DEFAULT_BONUS_PRIORITY, MAX_FREE_SPIN_GAMES } from '@/constants'

import {
  BonusByBundleType,
  MaxBet,
  MaxWin,
  MinResetRealMoneyAmount,
} from '@/state/bonus-engine/models'
import { authSelector } from '@/state/auth/slices/index.slices'
import useBonusTrigger from './use-bonus-trigger.hook'

import useBonusEngineFormActions from './use-be-offers-actions.hook'
import useBonusAmount from './use-bonus-amount.hook'
import useRolloverSettings from './use-rollover-settings.hook'

import useRestrictions from './use-restrictions.hook'
// import useEligibility from './use-eligibility.hook'
import {
  bonusDetailsSchema,
  bonusAmountSchema,
  enableSchema,
  triggerSchema,
  rolloverSettinsSchema,
  // eligibilitySchema,
  restrictionsSchema,
  freeSpinsSchema,
  dependenciesSchema,
  rolloverRestrictionsSchema,
} from '../components/schema'

import useFreeSpins from './use-free-spins.hook'
import useDependencies from './use-dependencies.hook'
import useBonusDetails from './use-bonus-details.hook'

import useEnable from './use-enable.hook'
import useRestrictionsSportsTypes from './use-restrictions-sports-types.hook'
import useRolloverRestrictions from './use-rollover-restrictions.hook'

export type StepProps = {
  label: string
  disable: boolean
  visible: boolean
}

const useSteps = () => {
  const { bonusDetails, setCurrentBonusDetails } = useBonusDetails()

  const { bonusTrigger, setCurrentBonusTrigger, handleOnChangePMethods } =
    useBonusTrigger()

  const { bonusAmount, setCurrentBonusAmount } = useBonusAmount()

  const { freeSpins, setCurrentFreeSpins } = useFreeSpins()

  const { rolloverSettings, setCurrentRolloverSettings } = useRolloverSettings({
    triggerBy: bonusTrigger.state.triggerBy.value,
    bonusType: bonusDetails.state.type.value,
  })

  const { getWagerTypeInitValues, getSportInitValues, getProductsInitValues } =
    useRestrictionsSportsTypes()

  const {
    restrictions,
    //setCurrentRestrictions,
    handleOnClickSetEnable,
    handleOnClickSetDisable,
    handleOnClickSetDiscardContribution,
    handleOnChangeCategory,
    handleOnChangeProvider,
    providersCategories,
    providers,
    getFullExpiration,
    getListGamesBetSoft,
    getListFreeSpinGames,
  } = useRestrictions({ bonusType: bonusDetails.state.type.value })

  const isFreeSpins = bonusDetails.state.type.value === BonusType.FREE_SPINS

  const hasBonusExpiration = () =>
    restrictions.state.expirationType.value === ExpirationType.TIME_UNIT
      ? Number(restrictions.state.expirationUnit.value) > 0
      : Boolean(restrictions.state.expirationDate.value)

  const {
    rolloverRestrictions,
    setCurrentRolloverRestrictions,
    handleOnClickSetEnableRollover,
    handleOnClickSetDisableRollover,
    handleOnClickSetDiscardContributionRollover,
  } = useRolloverRestrictions()

  // const { eligibility } = useEligibility()

  const {
    dependencies,
    setCurrentBonusDependencies,
    handleOnChangeBonusBundle,
  } = useDependencies()

  const { enable, setCurrentEnable } = useEnable()

  const { currentBonus, bonusFormMode, isLoading, bonusesByBundle } =
    useSelector(({ bonusEngine }: ApplicationState) => bonusEngine)

  const { brand } = useSelector(authSelector)

  const { createBonusProgramAction, updateBonusProgramAction } =
    useBonusEngineFormActions()

  const steps: StepProps[] = [
    {
      label: 'generalSettings',
      disable:
        bonusDetails.disable ||
        bonusDetails.state.name.value === '' ||
        bonusDetails.state.description.value === '' ||
        bonusDetails.state.code.value === '' ||
        bonusDetails.state.type.value === '',
      visible: true,
    },
    {
      label: 'triggerSettings',
      disable: bonusTrigger.disable,
      visible: true,
    },
    {
      label: 'bonusAmountSettings',
      disable: bonusAmount.disable,
      visible: true,
    },
    {
      label: 'rolloverSettings',
      disable: rolloverSettings.disable,
      visible: true,
    },
    {
      label: 'freeSpinsSettings',
      disable: freeSpins.disable || !freeSpins.state.freeSpinQuantity.value,
      visible: true,
    },
    {
      label: 'rolloverRestrictionsSettings',
      disable: restrictions.disable,
      visible: true,
    },
    {
      label: 'restrictionsSettings',
      disable:
        restrictions.disable ||
        (bonusDetails.state.type.value === BonusType.BETSOFT_CASINO &&
          (restrictions.state.games.value.length === 0 ||
            restrictions.state.providers.value.length === 0)) ||
        (bonusDetails.state.type.value === BonusType.CASINO_STANDARD &&
          restrictions.state.providers.value.length === 0) ||
        // BGaming needs games and a valid_until to issue the free spins
        (isFreeSpins &&
          (restrictions.state.games.value.length === 0 ||
            restrictions.state.games.value.length > MAX_FREE_SPIN_GAMES ||
            restrictions.state.providers.value.length === 0 ||
            !hasBonusExpiration())),
      visible: true,
    },
    // {
    //   label: 'eligibility',
    //   disable: eligibility.disable,
    //   visible: true,
    // },
    {
      label: 'dependencies',
      disable: dependencies.disable,
      visible: true,
    },

    {
      label: 'enableSettings',
      disable: enable.disable,
      visible: true,
    },
  ]

  let stepsFilters: StepProps[] = []

  switch (bonusDetails.state.type.value) {
    // case BonusType.FREE_PLAY:
    //   stepsFilters = steps.filter((step) => step.label !== 'freeSpinsSettings')
    //   break

    case BonusType.SPORTS_STANDARD:
    case BonusType.CASINO_STANDARD:
    case BonusType.BETSOFT_CASINO:
      stepsFilters = steps.filter(
        (step) =>
          step.label !== 'freeSpinsSettings' &&
          step.label !== 'rolloverRestrictionsSettings',
      )
      break

    case BonusType.FREE_SPINS:
      stepsFilters = steps.filter(
        (step) =>
          step.label !== 'bonusAmountSettings' &&
          step.label !== 'rolloverSettings' &&
          step.label !== 'rolloverRestrictionsSettings',
      )
      break

    default:
      stepsFilters = steps.filter((step) => step.label !== 'freeSpinsSettings')
      break
  }

  if (bonusTrigger.state.triggerBy.value === BonusTriggerType.BY_BUNDLE) {
    stepsFilters = stepsFilters.filter((step) => step.label !== 'dependencies')
  }

  const disableSubmit = stepsFilters.filter((step) => step.disable).length > 0

  const handleResetForm = () => {
    bonusDetails.setFormValues(bonusDetailsSchema)
    bonusDetails.setDisable(true)
    bonusTrigger.setFormValues(triggerSchema(brand.currencies))
    bonusTrigger.setDisable(true)
    bonusAmount.setFormValues(bonusAmountSchema(brand.currencies))
    bonusAmount.setDisable(true)
    rolloverSettings.setFormValues(rolloverSettinsSchema(brand.currencies))
    rolloverSettings.setDisable(true)
    rolloverRestrictions.setFormValues(
      rolloverRestrictionsSchema(brand.currencies),
    )
    rolloverRestrictions.setDisable(true)
    freeSpins.setFormValues(freeSpinsSchema)
    freeSpins.setDisable(true)
    restrictions.setFormValues(restrictionsSchema(brand.currencies))
    // eligibility.setFormValues(eligibilitySchema)
    dependencies.setFormValues(dependenciesSchema)
    enable.setFormValues(enableSchema)
  }

  const handleCreateBonusOffer = () => {
    const newProviders =
      restrictions.state.providers.value.length > 0
        ? restrictions.state.providers.value.map((item: any) => {
            const provider = {
              provider: item.value,
            }
            return provider
          })
        : []

    const newPaymentMethods =
      bonusTrigger.state.paymentMethods.value.length > 0
        ? bonusTrigger.state.paymentMethods.value.map((item: any) =>
            Number(item.index),
          )
        : []

    const conditionsTypes = [
      ...keys(TriggerConditions).map((x) => {
        return {
          index: TriggerConditions[x],
          label: x,
        }
      }),
    ]

    let condition = ''
    if (
      bonusTrigger.state.condition.value !== undefined &&
      bonusTrigger.state.condition.value !== ''
    ) {
      condition = conditionsTypes.filter(
        (subCondition) =>
          subCondition.label ===
          `${bonusTrigger.state.condition.value}`.toUpperCase(),
      )[0].index
    }

    let newRestrictions
    if (isFreeSpins) {
      // the bonus engine has no restrictions field for free spins, the games
      // travel as freeSpinGames instead
      newRestrictions = undefined
    } else if (
      bonusDetails.state.type.value === BonusType.SPORTS_STANDARD
      // || bonusDetails.state.type.value === BonusType.FREE_PLAY
    ) {
      newRestrictions = {
        /* TODO: We could use this rollover type in the future
        products:
          bonusDetails.state.type.value === BonusType.FREE_PLAY
            ? getProductsDefaultValues()
            : restrictions.state.products.value, */
        wagerTypes: restrictions.state.wagerTypes.value,
        sports: restrictions.state.sports.value,
        line: {
          min: Number(restrictions.state.minLine.value),
          max: Number(restrictions.state.maxLine.value),
        },
      }
    } else if (bonusDetails.state.type.value === BonusType.BETSOFT_CASINO) {
      newRestrictions = {
        games: getListGamesBetSoft(),
      }
    }

    const restrictionMaxBet: MaxBet[] = []
    brand.currencies.forEach((currency) => {
      if (
        restrictions.state[`maxBet_${currency}`].value !== '' &&
        restrictions.state[`maxBet_${currency}`].value !== undefined
      ) {
        restrictionMaxBet.push({
          maxBet: Number(restrictions.state[`maxBet_${currency}`].value),
          currency,
        })
      }
    })

    if (restrictionMaxBet.length > 0) {
      brand.currencies.forEach((currency) => {
        if (!restrictionMaxBet.find((maxBet) => maxBet.currency === currency)) {
          restrictionMaxBet.push({
            maxBet: 0,
            currency,
          })
        }
      })
    }

    const restrictionMaxWin: MaxWin[] = []
    brand.currencies.forEach((currency) => {
      if (
        restrictions.state[`maxWin_${currency}`].value !== '' &&
        restrictions.state[`maxWin_${currency}`].value !== undefined
      ) {
        restrictionMaxWin.push({
          maxWin: Number(restrictions.state[`maxWin_${currency}`].value),
          currency,
        })
      }
    })

    if (restrictionMaxWin.length > 0) {
      brand.currencies.forEach((currency) => {
        if (!restrictionMaxWin.find((maxWin) => maxWin.currency === currency)) {
          restrictionMaxWin.push({
            maxWin: 0,
            currency,
          })
        }
      })
    }

    // Rollover Restrictions
    let newRolloverRestriction
    // if (bonusDetails.state.type.value === BonusType.FREE_PLAY) {
    //   // Min Reset Real Money Amount
    //   const minResetRealMoneyAmount: MinResetRealMoneyAmount[] = []
    //   brand.currencies.forEach((currency) => {
    //     if (
    //       rolloverRestrictions.state[`minResetRealMoneyAmount_${currency}`]
    //         .value !== '' &&
    //       rolloverRestrictions.state[`minResetRealMoneyAmount_${currency}`]
    //         .value !== undefined
    //     ) {
    //       minResetRealMoneyAmount.push({
    //         amount: Number(
    //           rolloverRestrictions.state[`minResetRealMoneyAmount_${currency}`]
    //             .value,
    //         ),
    //         currency,
    //       })
    //     }
    //   })

    //   if (minResetRealMoneyAmount.length > 0) {
    //     brand.currencies.forEach((currency) => {
    //       if (
    //         !minResetRealMoneyAmount.find(
    //           (maxWin) => maxWin.currency === currency,
    //         )
    //       ) {
    //         minResetRealMoneyAmount.push({
    //           amount: 0,
    //           currency,
    //         })
    //       }
    //     })
    //   }

    //   newRolloverRestriction = {
    //     /* TODO: We could use this rollover type in the future
    //     products:
    //       bonusDetails.state.type.value === BonusType.FREE_PLAY
    //         ? getProductsDefaultValues()
    //         : rolloverRestrictions.state.products.value, */
    //     wagerTypes: rolloverRestrictions.state.wagerTypes.value,
    //     sports: rolloverRestrictions.state.sports.value,
    //     line: {
    //       min: Number(rolloverRestrictions.state.minLine.value),
    //       max: Number(rolloverRestrictions.state.maxLine.value),
    //     },
    //     rolloverAmountCalculationType:
    //       rolloverRestrictions.state.rolloverAmountCalculationType.value,
    //     minResetRealMoneyAmount:
    //       minResetRealMoneyAmount.length > 0 &&
    //       process.env.REACT_APP_STAGE === 'stg'
    //         ? minResetRealMoneyAmount
    //         : undefined,
    //   }
    // }

    // Max Bonuses Allowed

    const maxBonusesAllowed = restrictions.state.maxBonusesAllowed.value
      ? Number(restrictions.state.maxBonusesAllowed.value)
      : 0

    // an empty priority field keeps the bonus on the default weight
    const priority =
      bonusDetails.state.priority.value !== '' &&
      bonusDetails.state.priority.value !== undefined
        ? Number(bonusDetails.state.priority.value)
        : DEFAULT_BONUS_PRIORITY

    const newBonusEngine = {
      name: bonusDetails.state.name.value,
      description: bonusDetails.state.description.value,
      type: bonusDetails.state.type.value,
      priority,
      bonusPeriod:
        bonusDetails.state.recurrence.value !== 'noPeriod' &&
        bonusDetails.state.recurrence.value !== '' &&
        bonusDetails.state.recurrence.value !== undefined
          ? {
              recurrence: bonusDetails.state.recurrence.value,
              startDate:
                bonusDetails.state.recurrence.value ===
                  BonusPeriodRecurrenceEnum.daily ||
                bonusDetails.state.recurrence.value ===
                  BonusPeriodRecurrenceEnum.weekday
                  ? bonusDetails.state.startTime.value
                  : getIsoLocalTimeFromDateTime(
                      bonusDetails.state.startDate.value,
                      bonusDetails.state.startTime.value,
                    ),
              endDate:
                bonusDetails.state.recurrence.value ===
                  BonusPeriodRecurrenceEnum.daily ||
                bonusDetails.state.recurrence.value ===
                  BonusPeriodRecurrenceEnum.weekday
                  ? bonusDetails.state.endTime.value
                  : getIsoLocalTimeFromDateTime(
                      bonusDetails.state.endDate.value,
                      bonusDetails.state.endTime.value,
                      true,
                    ),
              weekday:
                bonusDetails.state.recurrence.value ===
                BonusPeriodRecurrenceEnum.weekday
                  ? {
                      recurrence: bonusDetails.state.recurrenceWeekday.value,
                      day: bonusDetails.state.day.value,
                    }
                  : undefined,
            }
          : undefined,
      // BGaming enforces its own limits, free spins carry no max bet or max win
      maxBet:
        !isFreeSpins && restrictionMaxBet.length > 0
          ? restrictionMaxBet
          : undefined,
      maxWin:
        !isFreeSpins && restrictionMaxWin.length > 0
          ? restrictionMaxWin
          : undefined,
      trigger:
        bonusTrigger.state.triggerBy.value ===
          BonusTriggerType.DEPOSIT_BY_CODE ||
        bonusTrigger.state.triggerBy.value === BonusTriggerType.DEPOSIT_BY_CLICK
          ? {
              triggerBy: Number(bonusTrigger.state.triggerBy.value),
              prerequisites: {
                depositCount:
                  bonusTrigger.state.depositCount.value !== ''
                    ? Number(bonusTrigger.state.depositCount.value)
                    : '',
                condition,
              },
              minimumDepositAmount: brand.currencies.map((currency) => {
                return {
                  minimumDepositAmount: Number(
                    bonusTrigger.state[`minimumDepositAmount_${currency}`]
                      .value || 0,
                  ),
                  currency,
                }
              }),
              paymentMethods: newPaymentMethods,
            }
          : {
              triggerBy: Number(bonusTrigger.state.triggerBy.value),
              prerequisites:
                bonusTrigger.state.triggerBy.value === BonusTriggerType.BY_CODE
                  ? {
                      depositCount:
                        bonusTrigger.state.depositCount.value !== ''
                          ? Number(bonusTrigger.state.depositCount.value)
                          : '',
                      condition,
                    }
                  : undefined,
            },
      bonusAmount:
        bonusDetails.state.type.value !== BonusType.FREE_SPINS
          ? brand.currencies.map((currency) => {
              return {
                percentage:
                  bonusAmount.state.amountType.value === 'PERCENTAGE'
                    ? Number(
                        bonusAmount.state[`percentage_${currency}`].value || 0,
                      )
                    : 0,
                cap:
                  bonusAmount.state.amountType.value === 'PERCENTAGE'
                    ? Number(bonusAmount.state[`cap_${currency}`].value || 0)
                    : 0,
                fixed:
                  bonusAmount.state.amountType.value === 'FIXED'
                    ? Number(bonusAmount.state[`fixed_${currency}`].value || 0)
                    : 0,
                currency,
              }
            })
          : undefined,
      freeSpinQuantity: isFreeSpins
        ? Number(freeSpins.state.freeSpinQuantity.value)
        : undefined,
      freeSpinGames: isFreeSpins ? getListFreeSpinGames() : undefined,
      mode:
        rolloverSettings.state.mode.value !== '' &&
        bonusDetails.state.type.value !== BonusType.FREE_SPINS
          ? rolloverSettings.state.mode.value
          : undefined,
      rollover:
        bonusDetails.state.type.value !== BonusType.FREE_SPINS
          ? Number(rolloverSettings.state.rollover.value)
          : undefined,
      rolloverCoefficcient:
        rolloverSettings.state.rolloverCoefficcient.value !== 0 &&
        bonusDetails.state.type.value !== BonusType.FREE_SPINS
          ? Number(rolloverSettings.state.rolloverCoefficcient.value)
          : undefined,
      wageringOrder:
        rolloverSettings.state.mode.value !== '' &&
        rolloverSettings.state.mode.value === BonusModeType.NON_STICKY &&
        bonusDetails.state.type.value !== BonusType.FREE_SPINS
          ? {
              order: rolloverSettings.state.order.value,
              rmMinAmount:
                rolloverSettings.state.order.value ===
                BonusWageringOrderType.RM_FIRST
                  ? brand.currencies.map((currency) => {
                      return {
                        rmMinAmount: Number(
                          rolloverSettings.state[`rmMinAmount_${currency}`]
                            .value || 0,
                        ),
                        currency,
                      }
                    })
                  : undefined,
            }
          : undefined,
      // eligibility: {
      //   promotionalCodesAvailables:
      //     eligibility.state.promotionalCodesAvailables.value.join(',') || '',
      //   promotionalCodesUnavailables:
      //     eligibility.state.promotionalCodesUnavailables.value.join(',') || '',
      //   creditCardTiersAvailables:
      //     eligibility.state.creditCardTiersAvailables.value.join(',') || '',
      //   creditCardTiersUnavailables:
      //     eligibility.state.creditCardTiersUnavailables.value.join(',') || '',
      // },
      // agents: {
      //   agents: eligibility.state.agents.value,
      //   enable: eligibility.state.isEnable.value || false,
      // },
      // countries: {
      //   countries: eligibility.state.countries.value,
      //   enable: eligibility.state.includeCountry.value || false,
      // },
      providers: [...newProviders],
      restrictions: newRestrictions,
      maxBonusesAllowed: maxBonusesAllowed,
      freePlayRolloverRestrictions: newRolloverRestriction,
      bonusBundle:
        bonusTrigger.state.triggerBy.value !== BonusTriggerType.BY_BUNDLE
          ? dependencies.state.bonusBundle.value &&
            dependencies.state.bonusBundle.value.length > 0
            ? dependencies.state.bonusBundle.value.map(
                (item: BonusByBundleType) => item._id,
              )
            : []
          : [],
      bonusExpiration:
        restrictions.state.expirationType.value === ExpirationType.TIME_UNIT
          ? restrictions.state.expirationUnit.value !== ''
            ? restrictions.state.expirationUnit.value
            : undefined
          : getFullExpiration(),
      isActive: enable.state.isActive.value,
      // isMakeUpRule: enable.state.isMakeUpRule.value,
    }

    bonusFormMode === FormStatusType.CREATE ||
    bonusFormMode === FormStatusType.DUPLICATE
      ? createBonusProgramAction({
          ...newBonusEngine,
          code: bonusDetails.state.code.value,
        })
      : updateBonusProgramAction({
          ...newBonusEngine,
          _id: currentBonus._id,
        })
  }

  useEffect(() => {
    if (currentBonus !== undefined) {
      setCurrentBonusDetails()
      setCurrentBonusTrigger()
      setCurrentBonusAmount()
      setCurrentRolloverSettings()
      //setCurrentRestrictions()
      setCurrentRolloverRestrictions()
      // setCurrentEligibility()
      setCurrentEnable()
      setCurrentFreeSpins()
    }
  }, [currentBonus]) // eslint-disable-line

  useEffect(() => {
    setCurrentBonusDependencies()
  }, [bonusesByBundle]) // eslint-disable-line

  useEffect(() => {
    if (
      bonusDetails.state.type.value === BonusType.FREE_SPINS &&
      bonusFormMode === FormStatusType.CREATE
    ) {
      rolloverSettings.setFormValues(rolloverSettinsSchema(brand.currencies))
    }

    restrictions.setFormValues({
      ...restrictions.state,
      providers: {
        value: [],
        error: '',
      },
      // games belong to the provider that was selected, and they are identified
      // differently per bonus type, so they cannot survive a type change
      games: {
        value: [],
        error: '',
      },
    })
  }, [bonusDetails.state.type.value]) // eslint-disable-line

  return {
    steps: stepsFilters,
    bonusDetails,
    bonusTrigger,
    handleOnChangePMethods,
    bonusAmount,
    rolloverSettings,
    freeSpins,
    restrictions,
    handleOnClickSetEnable,
    handleOnClickSetDisable,
    handleOnClickSetDiscardContribution,
    getProductsInitValues,
    getWagerTypeInitValues,
    getSportInitValues,
    providersCategories,
    providers,
    handleOnChangeCategory,
    handleOnChangeProvider,
    // eligibility,
    handleResetForm,
    disableSubmit,
    handleCreateBonusOffer,
    enable,
    isLoading,
    dependencies,
    handleOnChangeBonusBundle,
    rolloverRestrictions,
    handleOnClickSetEnableRollover,
    handleOnClickSetDisableRollover,
    handleOnClickSetDiscardContributionRollover,
  }
}

export default useSteps
